import { useEffect, useRef, useState } from "react";
import Auth from "./utils/auth";

const API = process.env.REACT_APP_API_URL || "http://localhost:3001";
const MAX_BYTES = 5 * 1024 * 1024;
export const IMAGINE_TYPES = ["image/jpeg", "image/png", "image/webp"];

async function api(path, options = {}) {
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${Auth.getToken()}`,
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

function cancelRequest(requestId) {
  api("/imagine/cancel", { method: "POST", body: JSON.stringify({ requestId }) }).catch(() => {});
}

export default function useImagine(character) {
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | working | done | error
  const [message, setMessage] = useState("");
  const [result, setResult] = useState(null);
  const [imageRequestId, setImageRequestId] = useState(null);
  const [videoPhase, setVideoPhase] = useState("idle"); // idle | working | done | error
  const [videoMessage, setVideoMessage] = useState("");
  const [videoUrl, setVideoUrl] = useState(null);
  const pollRef = useRef(null);
  const runRef = useRef(0); // bumps on every new run or cancel, so stale async work drops out
  const activeIdRef = useRef(null); // Higgsfield request currently being waited on

  useEffect(
    () => () => {
      runRef.current++;
      clearTimeout(pollRef.current);
    },
    []
  );

  function pick(e) {
    const f = e.target.files?.[0];
    e.target.value = ""; // lets the same file be picked again later
    if (!f) return;
    if (!IMAGINE_TYPES.includes(f.type) || f.size > MAX_BYTES) {
      setPhase("error");
      setMessage("Use a JPG, PNG or WebP under 5 MB.");
      return;
    }
    setFile(f);
    setPhase((prev) => (prev === "error" ? "idle" : prev));
    setMessage("");
  }

  async function generate() {
    if (!file || !character) return;
    clearTimeout(pollRef.current);
    const run = ++runRef.current;
    setPhase("working");
    setMessage("Uploading…");
    setResult(null);
    setVideoUrl(null);
    setVideoPhase("idle");
    try {
      const { uploadUrl, key } = await api("/imagine/upload-url", {
        method: "POST",
        body: JSON.stringify({ contentType: file.type, size: file.size }),
      });
      if (run !== runRef.current) return;
      const put = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!put.ok) throw new Error("Upload failed. Try again.");
      if (run !== runRef.current) return;
      setMessage("Casting…");
      const { requestId } = await api("/imagine/generate", {
        method: "POST",
        body: JSON.stringify({ key, character }),
      });
      if (run !== runRef.current) {
        cancelRequest(requestId); // canceled while the request was being created
        return;
      }
      activeIdRef.current = requestId;
      setImageRequestId(requestId);
      poll(requestId, run, Date.now() + 10 * 60_000, "image");
    } catch (err) {
      if (run !== runRef.current) return;
      setPhase("error");
      setMessage(err.message);
    }
  }

  async function makeVideo() {
    if (!imageRequestId) return;
    clearTimeout(pollRef.current);
    const run = ++runRef.current;
    setVideoPhase("working");
    setVideoMessage("");
    setVideoUrl(null);
    try {
      const { requestId } = await api("/imagine/video", {
        method: "POST",
        body: JSON.stringify({ imageRequestId, character }),
      });
      if (run !== runRef.current) {
        cancelRequest(requestId);
        return;
      }
      activeIdRef.current = requestId;
      poll(requestId, run, Date.now() + 15 * 60_000, "video");
    } catch (err) {
      if (run !== runRef.current) return;
      setVideoPhase("error");
      setVideoMessage(err.message);
    }
  }

  function poll(requestId, run, deadline, kind) {
    const fail = (text) => {
      activeIdRef.current = null;
      if (kind === "video") {
        setVideoPhase("error");
        setVideoMessage(text);
      } else {
        setPhase("error");
        setMessage(text);
      }
    };
    pollRef.current = setTimeout(async () => {
      try {
        const s = await api(`/imagine/status/${requestId}`);
        if (run !== runRef.current) return;
        const url = kind === "video" ? s.videoUrl : s.imageUrl;
        if (s.status === "completed" && url) {
          activeIdRef.current = null;
          if (kind === "video") {
            setVideoUrl(url);
            setVideoPhase("done");
          } else {
            setResult(url);
            setPhase("done");
          }
          return;
        }
        if (s.status === "nsfw") return fail("That photo couldn't be used. Try a different one.");
        if (s.status === "failed" || s.status === "canceled") return fail("Generation failed. Try again.");
        if (Date.now() > deadline) return fail("Timed out. Try again.");
        poll(requestId, run, deadline, kind);
      } catch (err) {
        if (run !== runRef.current) return;
        fail(err.message);
      }
    }, 4000);
  }

  function stopActive() {
    runRef.current++;
    clearTimeout(pollRef.current);
    const id = activeIdRef.current;
    activeIdRef.current = null;
    if (id) cancelRequest(id);
  }

  function cancel() {
    stopActive();
    setPhase((p) => (p === "working" ? "idle" : p));
    setVideoPhase((p) => (p === "working" ? "idle" : p));
    setMessage("");
    setVideoMessage("");
  }

  function restore() {
    stopActive();
    setFile(null);
    setResult(null);
    setImageRequestId(null);
    setVideoUrl(null);
    setVideoPhase("idle");
    setPhase("idle");
    setMessage("");
  }

  return { file, phase, message, result, videoPhase, videoMessage, videoUrl, pick, generate, makeVideo, cancel, restore };
}